import { invokeBackendRoute } from '@/backend/invoke';
import type { GetFirmInfoForHandle } from '@cloudFunctions/types/getFirmInfoForHandle';
import type { GetCurrentClient } from '@cloudFunctions/types/clients/getCurrentClient';
import type { SendClientMagicLink } from '@cloudFunctions/types/clients/sendClientMagicLink';
import type { SendClientPasswordResetLink } from '@cloudFunctions/types/clients/sendClientPasswordResetLink';
import type { GetPaymentPageUnauthenticated } from '@cloudFunctions/types/clientToFirmPayments/getPaymentPageUnauthenticated';
import type { SubmitPaymentFromClientToFirm } from '@cloudFunctions/types/clientToFirmPayments/submitPaymentFromClientToFirm';
import type { DisablePaymentInstrument } from '@cloudFunctions/functions/payments/disablePaymentInstrument';
import type { GetPaymentInstrumentsForFirmClient } from '@cloudFunctions/types/clientToFirmPayments/getPaymentInstrumentsForFirmClient';
import type { SetDefaultPaymentInstrumentIdForMatter } from '@cloudFunctions/types/clientToFirmPayments/setDefaultPaymentInstrumentIdForMatter';

export type {
  GetFirmInfoForHandle,
  GetCurrentClient,
  GetPaymentPageUnauthenticated,
  SubmitPaymentFromClientToFirm,
  GetPaymentInstrumentsForFirmClient,
};

export const getFirmInfoForHandle = async (
  props: GetFirmInfoForHandle.Props,
): Promise<GetFirmInfoForHandle.Result> => {
  return invokeBackendRoute<
    GetFirmInfoForHandle.Result,
    GetFirmInfoForHandle.Props
  >('getFirmInfoForHandle', props);
};

export const getCurrentClient = async (
  props: GetCurrentClient.RequestData,
): Promise<GetCurrentClient.Result> => {
  return invokeBackendRoute<
    GetCurrentClient.Result,
    GetCurrentClient.RequestData
  >('getCurrentClient', props);
};

export const sendClientMagicLink = async (
  props: SendClientMagicLink.Props,
): Promise<SendClientMagicLink.Result> => {
  return invokeBackendRoute<
    SendClientMagicLink.Result,
    SendClientMagicLink.Props
  >('sendClientMagicLink', props);
};

export const sendClientPasswordResetLink = async (
  props: SendClientPasswordResetLink.Props,
): Promise<SendClientPasswordResetLink.Result> => {
  return invokeBackendRoute<
    SendClientPasswordResetLink.Result,
    SendClientPasswordResetLink.Props
  >('sendClientPasswordResetLink', props);
};

export const disablePaymentInstrument = async (
  requestData: DisablePaymentInstrument.RequestData,
): Promise<DisablePaymentInstrument.Result> => {
  return invokeBackendRoute<
    DisablePaymentInstrument.Result,
    DisablePaymentInstrument.RequestData
  >('disablePaymentInstrument', requestData);
};

export const getPaymentPageUnauthenticated = async (
  props: GetPaymentPageUnauthenticated.Props,
): Promise<GetPaymentPageUnauthenticated.Result> => {
  // The HTTP route at `/api/public/payment-pages/:paymentPageId` is served
  // from the same Firebase Hosting origin (`clients.practist.io`) that the
  // legacy `httpsCallableFromURL` workaround relied on, so the
  // Mainland-China-bypass behavior is preserved without an explicit
  // `import.meta.env.PROD` branch. In dev the Vite proxy forwards `/api` to
  // the local api-core-node. See firebase.json `clients` rewrites.
  return invokeBackendRoute<
    GetPaymentPageUnauthenticated.Result,
    GetPaymentPageUnauthenticated.Props
  >('getPaymentPageUnauthenticated', props);
};

export const submitPaymentFromClientToFirm = async (
  props: SubmitPaymentFromClientToFirm.RequestData,
): Promise<SubmitPaymentFromClientToFirm.Result> => {
  return invokeBackendRoute<
    SubmitPaymentFromClientToFirm.Result,
    SubmitPaymentFromClientToFirm.RequestData
  >('submitPaymentFromClientToFirm', props);
};

export const getPaymentInstrumentsForFirmClient = async (
  requestData: GetPaymentInstrumentsForFirmClient.RequestData,
): Promise<GetPaymentInstrumentsForFirmClient.Result> => {
  return invokeBackendRoute<
    GetPaymentInstrumentsForFirmClient.Result,
    GetPaymentInstrumentsForFirmClient.RequestData
  >('getPaymentInstrumentsForFirmClient', requestData);
};

export const setDefaultPaymentInstrumentIdForMatter = async (
  requestData: SetDefaultPaymentInstrumentIdForMatter.RequestData,
): Promise<SetDefaultPaymentInstrumentIdForMatter.Result> => {
  return invokeBackendRoute<
    SetDefaultPaymentInstrumentIdForMatter.Result,
    SetDefaultPaymentInstrumentIdForMatter.RequestData
  >('setDefaultPaymentInstrumentIdForMatter', requestData);
};
